import { Cache, namespace } from '../../../../../FrontendBundle/Resources/js/ofcs';
import Layer from './layer/layer';
import Transform from './render/transform';

/**
 * The PathFactory is used to create path elements for a given item. There
 * are several forms of paths like the bag path or a path with security
 * margins. There is exactly one method for each path type.
 *
 *     var svg         = canvas.getSvgWrapper(),
 *         layer       = canvas.getLayerManager().getElement('bags'),
 *         pathFactory = new PathFactory(svg);
 *
 *     this.bag = pathFactory.createBagPath(layer, item);
 *
 * The PathFactory needs two important references:
 *
 * 1. The *foam object* to resolve security margins for a given bag depth
 * 2. The *svg wrapper* of jQuery.svg to crate the path element
 */
export default class PathFactory {
    /**
     * Instance of svg wrapper
     *
     * @type {SVGWrapper}
     */
    svg = null;

    /**
     * @type {Cache}
     */
    cache = new Cache();

    /**
     * @type {Object}
     */
    watchedItems = {};

    /**
     * Initializes the path factory
     *
     * @constructor
     * @param {SVGWrapper} svg SVG Wrapper of jQuery.svg library
     */
    constructor(svg) {
        this.svg = svg;
    }

    /**
     * Clear cache on change.
     *
     * @param {AbstractItem} item
     */
    clearCache(item) {
        const { cid, changed: changes } = item;

        if (changes.width !== undefined || changes.height !== undefined || changes.safety !== undefined) {
            this.cache.remove(`bagPathString-${cid}`);
            this.cache.remove(`origPathString-${cid}`);
            this.cache.remove(`marginPathString-${cid}`);
            this.cache.remove(`innerCollisionPathString-${cid}`);
            this.cache.remove(`outerCollisionPathString-${cid}`);
        }

        if (changes.depth !== undefined || changes.toolVersion !== undefined) {
            this.cache.remove(`marginPathString-${cid}`);
        }

        if (changes.rotation !== undefined || changes.position !== undefined || changes.flipped !== undefined) {
            this.cache.remove(`transformString-${cid}`);
            this.cache.remove(`absoluteTransformString-${cid}`);
        }

        if (changes.outlinePath !== undefined) {
            this.cache.remove(`cutPathString-${cid}`);
        }
    }

    /**
     * Register on change event.
     *
     * @param {ItemInterface} item
     */
    watchItem(item) {
        if (!this.watchedItems[item.cid]) {
            item.on('change', this.clearCache, this);
            this.watchedItems[item.cid] = true;
        }
    }

    /**
     * Returns current svg wrapper
     *
     * @return {SVGWrapper} Instance of svg wrapper
     */
    getSvgWrapper() {
        return this.svg;
    }

    /**
     * Returns bag path string for an item
     *
     * @param {ItemInterface} item Item to get values from
     * @return {string} Path string to use with svg path element
     */
    getBagPathString(item) {
        return this.cache.get(`bagPathString-${item.cid}`, () => {
            this.watchItem(item);
            return this.getPathString(item.get('ext'));
        });
    }

    /**
     * Returns original path string for an item
     *
     * @param {ItemInterface} item Item to get values from
     * @return {string} Path string to use with svg path element
     */
    getOriginalPathString(item) {
        return this.cache.get(`origPathString-${item.cid}`, () => {
            this.watchItem(item);
            return this.getPathString(item.get('orig'));
        });
    }

    /**
     * Returns sensor path string for an item
     *
     * @param {ItemInterface} item Item to get values from
     * @return {string} Path string to use with svg path element
     */
    getSensorPathString(item) {
        return this.cache.get(`sensorPathString-${item.cid}`, () => {
            this.watchItem(item);
            return this.getPathString(item.get('sensorPath'));
        });
    }

    /**
     * Returns safety margin path string for an item
     *
     * @param {ItemInterface} item Item to get values from
     * @return {string} Path string to use with svg path element
     */
    getMarginPathString(item) {
        return this.cache.get(`marginPathString-${item.cid}`, () => {
            this.watchItem(item);
            return this.getPathString(item.get('safety'));
        });
    }

    /**
     * Returns inner collision path string for an foam item
     *
     * @param {FoamItem} item Item to get values from
     * @return {string} Path string to use with svg path element
     */
    getInnerCollisionPathString(item) {
        return this.cache.get(
            `innerCollisionPathString-${item.cid}`,
            () => this.getPathString(item.get('innerCollision')),
        );
    }

    /**
     * Returns outer collision path string for an foam item
     *
     * @param {FoamItem} item Item to get values from
     * @return {string} Path string to use with svg path element
     */
    getOuterCollisionPathString(item) {
        return this.cache.get(
            `outerCollisionPathString-${item.cid}`,
            () => this.getPathString(item.get('outerCollision')),
        );
    }

    /**
     * Returns cut path string for an item
     *
     * @param {CutangleItem} item Item to get values from
     * @return {string} Path string to use with svg path element
     */
    getCutPathString(item) {
        return this.cache.get(`cutPathString-${item.cid}`, () => {
            this.watchItem(item);
            return this.getPathString(item.get('cutPath'));
        });
    }

    /**
     * Returns an array of bag lines for the given item.
     *
     * @param {ItemInterface} item Item to get lines for
     * @return {Array}
     * @deprecated
     */
    getBagLines(item) {
        return item.getBagLines();
    }

    /**
     * Returns an array of margin lines for the given item.
     *
     * @param {ItemInterface} item Item to get lines for
     * @return {Array}
     * @deprecated
     */
    getMarginLines(item) {
        return item.getMarginLines();
    }

    /**
     * Returns an array of bag lines for the given item.
     *
     * @param {ItemInterface} item Item to get lines for
     * @return {Array}
     * @deprecated
     */
    getOriginalLines(item) {
        return item.getOriginalLines();
    }

    /**
     * @param {jQuery|Layer} layer
     * @return {jQuery}
     * @private
     */
    _flattenLayer(layer) {
        if (layer instanceof Layer) {
            return layer.getElement();
        }
        return layer;
    }

    /**
     * Creates a bag path element.
     *
     * @param {jQuery|Layer} layer Layer which receives path element
     * @param {ItemInterface} item Editor item to create path for
     * @return {SVHPathElement}
     */
    createBagPath(layer, item) {
        return this.createPath(layer, item, this.getBagPathString(item), `bag-${item.cid}`);
    }

    /**
     * Creates a sensor path element
     *
     * @param {jQuery|Layer} layer Layer which receives path element
     * @param {ItemInterface} item Editor item to create path for
     * @return {SVGPathElement}
     */
    createSensorPath(layer, item) {
        return this.createPath(layer, item, this.getSensorPathString(item), `sensor-${item.cid}`);
    }

    /**
     * Creates a path element with security margin.
     *
     * @param {jQuery|Layer} layer Layer which receives path element
     * @param {ItemInterface} item Editor item to create path for
     * @return {SVGPathElement}
     */
    createMarginPath(layer, item) {
        return this.createPath(layer, item, this.getMarginPathString(item), `margin-${item.cid}`);
    }

    /**
     * Creates a path element for original path.
     *
     * @param {jQuery|Layer} layer Layer which receives path element
     * @param {ItemInterface} item Editor item to create path for
     * @return {SVGPathElement}
     */
    createOriginalPath(layer, item) {
        return this.createPath(layer, item, this.getOriginalPathString(item), `original-path-${item.cid}`);
    }

    /**
     * Creates a path element for inner collision path.
     *
     * @param {jQuery|Layer} layer Layer which receives path element
     * @param {FoamItem} item Editor item to create path for
     * @return {SVGPathElement}
     */
    createInnerCollisonPath(layer, item) {
        return this.createPath(layer, item, this.getInnerCollisionPathString(item), `inner-collision-${item.cid}`);
    }

    /**
     * Creates a path element for outer collision path.
     *
     * @param {jQuery|Layer} layer Layer which receives path element
     * @param {FoamItem} item Editor item to create path for
     * @return {SVGPathElement}
     */
    createOuterCollisonPath(layer, item) {
        return this.createPath(layer, item, this.getOuterCollisionPathString(item), `outer-collision-${item.cid}`);
    }

    /**
     * Creates a path element for a cut path.
     *
     * @param {jQuery} layer Layer which receives path element
     * @param {CutangleItem} item Editor item to create path for
     * @return {SVGPathElement}
     */
    createCutPath(layer, item) {
        return this.createPath(layer, item, this.getCutPathString(item), `cut-${item.cid}`);
    }

    /**
     * Insert a new path element with given path into given layer.
     *
     * @protected
     * @param {jQuery|Layer} layer Layer which receives path element
     * @param {ItemInterface} item Editor item to create path for
     * @param {string} pathString SVG path string
     * @param {string} id ID
     * @return {SVGPathElement} Created path element
     */
    createPath(layer, item, pathString, id) {
        const svg = this.getSvgWrapper();

        return svg.path(this._flattenLayer(layer), pathString, { id });
    }

    /**
     * Returns a svg transform string. The transform string is extracted
     * from a transformation matrix. The matrix will translate a element
     * to the position of #item. If #item has a rotation value, the rotation
     * will also be added to the transformation matrix.
     *
     * @param {ItemInterface} item Item to get rotation and position
     * @return {string} Transformation string to use with SVG's `transform` attribute
     */
    getTransformString(item) {
        return this.cache.get(`transformString-${item.cid}`, () => {
            this.watchItem(item);

            const position = item.get('position');
            const rotation = item.get('rotation');
            const isFlipped = item.supportsFlipping && item.get('flipped');

            let transform = new Transform();

            if (position) {
                transform = transform.translatePoint(position);
            }

            if (rotation) {
                transform = transform.rotate(rotation);
            }

            if (isFlipped) {
                transform = transform.scale(1, -1);
            }

            return transform;
        });
    }

    getAbsoluteTransformString(item, force = false) {
        const key = `absoluteTransformString-${item.cid}`;

        if (force) this.cache.remove(key);

        return this.cache.get(key, () => {
            this.watchItem(item);

            const position = item.getAbsolutePosition();
            const rotation = item.get('rotation');
            const isFlipped = item.supportsFlipping && item.get('flipped');

            let transform = new Transform();

            if (position) {
                transform = transform.translatePoint(position);
            }

            if (rotation) {
                transform = transform.rotate(rotation);
            }

            if (isFlipped) {
                transform = transform.scale(1, -1);
            }

            return transform;
        });
    }

    /**
     * Returns a svg transform string. The transform string is extracted
     * from a transformation matrix. The matrix will translate a element
     * to the position of #item. If #item has a rotation value, the rotation
     * will also be added to the transformation matrix.
     *
     * Thsi methods supports flipping.
     *
     * @param {ItemInterface} item Item to get rotation and position
     * @return {string} Transformation string to use with SVG's `transform` attribute
     */
    getTransformStringForImage(item) {
        return this.getTransformString(item);
    }

    /**
     * Creates a svg path string from a given segments array
     *
     * @protected
     * @param {Array} segments Array with svg path segments
     * @return {string} SVG path string
     */
    getPathString(segments) {
        let pathString = '';

        if (!segments) return pathString;

        for (let i = 0; i < segments.length; i += 1) {
            for (let j = 0; j < segments[i].length; j += 1) {
                pathString += segments[i][j][0];

                if (segments[i][j][0] === 'A' || segments[i][j][0] === 'a') {
                    pathString += [
                        `${segments[i][j][1]},${segments[i][j][2]}`,
                        segments[i][j][3],
                        `${segments[i][j][4]},${segments[i][j][5]}`,
                        `${segments[i][j][6]},${segments[i][j][7]}`,
                    ].join(' ');
                } else {
                    pathString += `${segments[i][j][1]},${segments[i][j][2]}`;
                }
            }
        }

        return pathString;
    }
}

namespace('Ofcs.Editor.Svg');
window.Ofcs.Editor.Svg.PathFactory = PathFactory;
